import { useState } from "react";
import { MainCard, MainCardTitle } from "src/components/main-card-type.tsx";
import { normalSanatizerBRL } from "src/utils/functions";
import type { RotativoResultsType, RotativoAlerta, RotativoBreakdown, RotativoCalculoMes, RotativoComparacao } from "../calculadora-rotativo.d";

const formatarMoedaPtBR = (value: number): string => {
  return value.toFixed(2).replace('.', ',');
};

const formatarPercentual = (value: number): string => {
  return (value * 100).toFixed(2).replace('.', ',');
};

const Tooltip = ({ children, text }: { children: React.ReactNode, text: string }) => (
  <div className="relative group">
    {children}
    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-4 py-3 min-w-[280px] max-w-[400px] text-sm text-white bg-gray-900 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-30 text-center shadow-lg">
      <div className="whitespace-normal leading-relaxed">
        {text}
      </div>
      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
    </div>
  </div>
);

const AlertCard = ({ alerta }: { alerta: RotativoAlerta }) => {
  const getAlertStyles = (tipo: string) => {
    switch (tipo) {
      case 'danger':
        return 'bg-red-50 border-red-300 text-red-800';
      case 'warning':
        return 'bg-yellow-50 border-yellow-300 text-yellow-800';
      default:
        return 'bg-gray-50 border-gray-300 text-gray-800';
    }
  };

  return (
    <div className={`p-4 rounded-lg border mb-3 ${getAlertStyles(alerta.tipo)}`}>
      <h4 className="font-semibold text-sm mb-2 flex items-center">
        <span className="mr-2">{alerta.icone}</span>
        {alerta.titulo}
      </h4>
      <p className="text-sm leading-relaxed">{alerta.mensagem}</p>
    </div>
  );
};

const BreakdownDetalhado = ({ breakdown }: { breakdown: RotativoBreakdown }) => {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div className="mt-6">
      <button
        onClick={() => setShowDetails(!showDetails)}
        className="w-full p-4 bg-gray-50 hover:bg-gray-100 rounded-lg border text-left transition-colors duration-200"
      >
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-800 text-base">
            🧮 Como Chegamos Neste Valor
          </h3>
          <span className="text-gray-600 text-sm font-medium">
            {showDetails ? '▼ Ocultar Detalhes' : '▶ Ver Cálculo Detalhado'}
          </span>
        </div>
      </button>
      
      {showDetails && (
        <div className="mt-3 p-4 bg-gray-50 rounded-lg border">
          <div className="grid sm:grid-cols-2 gap-6 text-sm">
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Valores Base:</h4>
              <div className="space-y-2">
                <p className="text-gray-700">• Valor original da fatura: <span className="font-medium">R$ {normalSanatizerBRL(breakdown.valorOriginal)}</span></p>
                <p className="text-gray-700">• Valor pago: <span className="font-medium">R$ {normalSanatizerBRL(breakdown.valorPago)}</span></p>
                <p className="text-gray-700 font-semibold">• Saldo no rotativo: <span className="text-red-700">R$ {normalSanatizerBRL(breakdown.saldoRotativo)}</span></p>
                <p className="text-gray-700">• Taxa de juros: <span className="font-medium">{formatarPercentual(breakdown.taxaJurosMensal)}% ao mês</span></p>
                {breakdown.diasAtraso > 0 && (
                  <p className="text-red-700 font-semibold">• Dias de atraso: {breakdown.diasAtraso} dias</p>
                )}
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Composição dos Encargos:</h4>
              <div className="space-y-2">
                <p className="text-gray-700">• Juros rotativo: <span className="font-medium text-red-800">R$ {normalSanatizerBRL(breakdown.calculoDetalhado.jurosRotativo)}</span></p>
                <p className="text-gray-700">• IOF mensal (0,38%): <span className="font-medium text-red-800">R$ {normalSanatizerBRL(breakdown.calculoDetalhado.iofMensal)}</span></p>
                <p className="text-gray-700">• IOF diário: <span className="font-medium text-red-800">R$ {normalSanatizerBRL(breakdown.calculoDetalhado.iofDiario)}</span></p>
                {breakdown.calculoDetalhado.multa > 0 && (
                  <p className="text-gray-700">• Multa por atraso: <span className="font-medium text-red-700">R$ {normalSanatizerBRL(breakdown.calculoDetalhado.multa)}</span></p>
                )}
                {breakdown.calculoDetalhado.jurosMora > 0 && (
                  <p className="text-gray-700">• Juros de mora: <span className="font-medium text-red-700">R$ {normalSanatizerBRL(breakdown.calculoDetalhado.jurosMora)}</span></p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ComparacaoCenarios = ({ comparacao, primeiroMes }: { comparacao: RotativoComparacao, primeiroMes: RotativoCalculoMes }) => (
  <div className="mt-8">
    <h3 className="font-bold text-primary text-lg mb-4 text-center">
      🔍 Comparação de Cenários (30 dias):
    </h3>
    
    <div className="grid sm:grid-cols-3 gap-4 grid-cols-1">
      <div className="p-4 bg-red-50 rounded-lg border border-red-200">
        <h4 className="font-semibold text-red-800 text-sm mb-2">Rotativo</h4>
        <p className="text-xs text-red-700 mb-3">{comparacao.cenarioMinimo.descricao}</p>
        <p className="text-sm font-bold text-red-800">R$ {normalSanatizerBRL(comparacao.cenarioMinimo.totalPagar)}</p>
        <p className="text-xs text-red-600">Juros: R$ {normalSanatizerBRL(comparacao.cenarioMinimo.totalJuros)}</p>
        <p className="text-xs text-red-600">Tempo: {comparacao.cenarioMinimo.tempo} mês</p>
      </div>
      
      <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
        <h4 className="font-semibold text-yellow-800 text-sm mb-2">Parcelamento</h4>
        <p className="text-xs text-yellow-700 mb-3">{comparacao.cenarioParcelamento.descricao}</p>
        <p className="text-sm font-bold text-yellow-800">R$ {normalSanatizerBRL(comparacao.cenarioParcelamento.totalPagar)}</p>
        <p className="text-xs text-yellow-600">Juros: R$ {normalSanatizerBRL(comparacao.cenarioParcelamento.totalJuros)}</p>
        <p className="text-xs text-yellow-600">Parcela: R$ {normalSanatizerBRL(comparacao.cenarioParcelamento.totalPagar / comparacao.cenarioParcelamento.tempo)}</p>
        <p className="text-xs text-yellow-600">Tempo: {comparacao.cenarioParcelamento.tempo} meses</p>
      </div>
      
      <div className="p-4 bg-green-50 rounded-lg border border-green-200">
        <h4 className="font-semibold text-green-800 text-sm mb-2">Quitação</h4>
        <p className="text-xs text-green-700 mb-3">{comparacao.cenarioQuitacao.descricao}</p>
        <p className="text-sm font-bold text-green-800">R$ {normalSanatizerBRL(comparacao.cenarioQuitacao.totalPagar)}</p>
        <p className="text-xs text-green-600">Juros: R$ {normalSanatizerBRL(comparacao.cenarioQuitacao.totalJuros)}</p>
        <p className="text-xs text-green-600">Tempo: Imediato</p>
      </div>
    </div>
    
    <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
      <p className="text-gray-700 text-sm">
      <strong>Análise:</strong> Se você quitar o saldo restante hoje (R$ {normalSanatizerBRL(comparacao.cenarioQuitacao.totalPagar)}), economizará&nbsp;
      <strong className="text-red-800">R$ {normalSanatizerBRL(Math.abs(primeiroMes.totalMes - comparacao.cenarioQuitacao.totalPagar))}</strong>
      &nbsp;em encargos do rotativo!
      </p>
    </div>

    <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
    <p className="text-blue-700 text-sm">
      <strong>Sobre o parcelamento da fatura:</strong> Taxa estimada baseada no mercado brasileiro (0,99% a 9,99% a.m.). 
      <strong> A taxa real varia conforme seu perfil de crédito e banco.</strong> Consulte sua instituição para condições específicas.
    </p>
    </div>
  </div>
);

export const RotativoResponse = ({ results }: { results: RotativoResultsType }) => {
  const { breakdown, calculoMeses, comparacao, alertas } = results;
  const primeiroMes = calculoMeses[0];
  
  if (!primeiroMes) return null;

  return (
    <MainCard className="mt-4">
      <MainCardTitle>Resultado - Rotativo em 30 dias</MainCardTitle>
      
      {/* Grid de resultados principais */}
      <div className="grid sm:grid-cols-3 gap-4 mb-6 grid-cols-1">
        <div className="p-3 bg-primary rounded border flex flex-col justify-center">
          <Tooltip text="Valor total da sua dívida após 30 dias no rotativo, incluindo juros, IOF e encargos por atraso. Este valor NÃO inclui a nova fatura do próximo mês.">
            <h2 className="sm:text-base text-sm text-center text-white font-semibold m-0 cursor-help">
              Valor da dívida em 30 dias
            </h2>
          </Tooltip>
          <p className="sm:text-xl text-center font-bold text-md text-white m-0 mt-2">
            <span className="text-sm mr-1 text-white">R$</span>
            {normalSanatizerBRL(primeiroMes.totalMes)}
          </p>
        </div>
        
        <div className="p-3 bg-white rounded border border-gray-300 flex flex-col justify-center">
          <Tooltip text="Valor original que não foi pago e entrou no crédito rotativo">
            <h2 className="sm:text-base text-sm text-center font-semibold m-0 cursor-help text-gray-700">
              Saldo em rotativo
            </h2>
          </Tooltip>
          <p className="sm:text-xl text-center font-bold text-md text-red-800 m-0 mt-2">
            <span className="text-sm mr-1 text-gray-500">R$</span>
            {normalSanatizerBRL(breakdown.saldoRotativo)}
          </p>
        </div>
        
        <div className="p-3 bg-white rounded border border-gray-300 flex flex-col justify-center">
          <Tooltip text="Percentual de crescimento da dívida em relação ao saldo original do rotativo">
            <h2 className="sm:text-base text-sm text-center font-semibold m-0 cursor-help text-gray-700">
              Crescimento da dívida
            </h2>
          </Tooltip>
          <p className="sm:text-xl text-center font-bold text-md text-red-700 m-0 mt-2">
            +{formatarMoedaPtBR(primeiroMes.percentualAumento)}%
          </p>
        </div>
      </div>

      {/* Alertas */}
      {alertas.length > 0 && (
        <div className="mb-6">
          <h2 className="font-bold text-primary text-lg mb-4">🚨 Alertas Importantes:</h2>
          {alertas.map((alerta, index) => (
            <AlertCard key={index} alerta={alerta} />
          ))}
        </div>
      )}

      {/* Resumo Detalhado */}
      <div className="mb-6 p-5 bg-gray-50 rounded-lg border">
        <h2 className="font-bold text-primary text-lg mb-4 text-center">
          Detalhamento dos 30 dias:
        </h2>
        
        <div className="grid sm:grid-cols-2 gap-6 grid-cols-1">
          <div className="p-4 bg-white rounded-lg border">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Composição dos Custos:</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Saldo rotativo:</span>
                <span className="font-medium">R$ {normalSanatizerBRL(breakdown.saldoRotativo)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Juros ({formatarPercentual(breakdown.taxaJurosMensal)}%):</span>
                <span className="text-red-600 font-medium">+R$ {normalSanatizerBRL(primeiroMes.jurosRotativo)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">IOF mensal:</span>
                <span className="text-red-600">+R$ {normalSanatizerBRL(primeiroMes.iofMensal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">IOF diário:</span>
                <span className="text-red-600">+R$ {normalSanatizerBRL(primeiroMes.iofDiario)}</span>
              </div>
              {primeiroMes.multa > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Multa por atraso:</span>
                  <span className="text-red-700 font-medium">+R$ {normalSanatizerBRL(primeiroMes.multa)}</span>
                </div>
              )}
              {primeiroMes.jurosMora > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Juros de mora:</span>
                  <span className="text-red-700 font-medium">+R$ {normalSanatizerBRL(primeiroMes.jurosMora)}</span>
                </div>
              )}
              <hr className="my-3 border-gray-300" />
              <div className="flex justify-between font-bold text-base">
                <span>Total após 30 dias:</span>
                <span className="text-red-800">R$ {normalSanatizerBRL(primeiroMes.totalMes)}</span>
              </div>
            </div>
          </div>
          
          <div className="p-4 bg-white rounded-lg border">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Impacto Financeiro:</h4>
            <div className="space-y-3 text-sm">
              <Tooltip text="Total de encargos pagos em 30 dias no rotativo">
                <div className="p-3 bg-red-50 rounded-lg cursor-help border border-red-200">
                  <p className="text-red-700 font-medium">Custo total dos encargos:</p>
                  <p className="text-red-800 font-bold text-base">
                    R$ {normalSanatizerBRL(primeiroMes.jurosRotativo + primeiroMes.iofMensal + primeiroMes.iofDiario + primeiroMes.multa + primeiroMes.jurosMora)}
                  </p>
                </div>
              </Tooltip>
              
              <Tooltip text="Percentual do valor original da fatura que se tornará encargos">
                <div className="p-3 bg-yellow-50 rounded-lg cursor-help border border-yellow-200">
                  <p className="text-yellow-700 font-medium">Percentual de encargos:</p>
                  <p className="text-yellow-800 font-bold text-base">
                    {formatarMoedaPtBR(((primeiroMes.totalMes - breakdown.saldoRotativo) / breakdown.valorOriginal) * 100)}% da fatura
                  </p>
                </div>
              </Tooltip>
              
              <Tooltip text="Custo adicional por usar o rotativo em vez de quitar à vista">
                <div className="p-3 bg-gray-50 rounded-lg cursor-help border border-gray-200">
                  <p className="text-gray-700 font-medium">Custo adicional do rotativo:</p>
                  <p className="text-gray-800 font-bold text-base">
                    R$ {normalSanatizerBRL(results.economiaQuitacao)}
                  </p>
                </div>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>

      <BreakdownDetalhado breakdown={breakdown} />

      <ComparacaoCenarios comparacao={comparacao} primeiroMes={primeiroMes} />

      {/* Orientações Educativas */}
      <div className="mt-8">
        <h2 className="font-bold text-primary text-lg mb-4 text-center">
        Como sair do rotativo:
        </h2>
        
        <div className="grid sm:grid-cols-2 gap-4 grid-cols-1">
          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <h4 className="font-semibold text-green-800 text-sm mb-3">Estratégias recomendadas</h4>
            <ul className="space-y-1 text-xs text-green-700">
              <li>• <strong>Negocie um parcelamento:</strong> Busque taxas menores que o rotativo</li>
              <li>• <strong>Antecipe o 13º:</strong> Use para quitar se possível</li>
              <li>• <strong>Venda bens desnecessários:</strong> Quite rapidamente</li>
              <li>• <strong>Renda extra:</strong> Freelances para pagamento</li>
              <li>• <strong>Renegocie com o banco:</strong> Peça desconto para quitação</li>
            </ul>
          </div>

          <div className="p-4 bg-red-50 rounded-lg border border-red-200">
            <h4 className="font-semibold text-red-800 text-sm mb-3">Evite armadilhas</h4>
            <ul className="space-y-1 text-xs text-red-700">
              <li>• <strong>Pagar apenas o mínimo:</strong> Dívida cresce exponencialmente</li>
              <li>• <strong>Fazer novos gastos:</strong> No cartão com dívida no rotativo</li>
              <li>• <strong>Ignorar o problema:</strong> Limite legal será atingido rapidamente</li>
              <li>• <strong>Empréstimos com juros altos:</strong> Para pagar o rotativo</li>
              <li>• <strong>Usar outros cartões:</strong> Para pagar o rotativo</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Informações Legais */}
      <div className="mt-6 grid sm:grid-cols-2 gap-4 grid-cols-1">
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="font-semibold text-gray-800 text-sm mb-2">Seus direitos</h4>
          <ul className="space-y-1 text-xs text-gray-700">
            <li>• <strong>Parcelamento obrigatório:</strong> Banco deve oferecer após 30 dias</li>
            <li>• <strong>Informações claras:</strong> Taxa de juros deve estar na fatura</li>
            <li>• <strong>Limite legal:</strong> 200% do valor original da fatura</li>
          </ul>
        </div>

        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="font-semibold text-gray-800 text-sm mb-2">Alternativas de Crédito</h4>
          <ul className="space-y-1 text-xs text-gray-700">
            <li>• <strong>Empréstimo pessoal:</strong> Negocie juros menores (2% a 8% ao mês)</li>
            <li>• <strong>Antecipação do 13º:</strong> Pela empresa</li>
            <li>• <strong>Empréstimo consignado:</strong> Opção disponível para CLT, servidores públicos e aposentados.</li>
          </ul>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="mt-6 p-4 bg-gray-100 rounded-lg border">
        <p className="text-sm text-gray-700">
          <strong>⚠️ Importante:</strong> Limite de 30 dias: após este período, a dívida será parcela pela instituição bancária. 
          Este cálculo mostra o valor da dívida após 30 dias (sem incluir novas movimentações). 
        </p>
      </div>

      {/* Info sobre taxa quando aplicável */}
      {breakdown.taxaJurosMensal > 0.10 && (
        <div className="mt-4 p-4 bg-red-50 rounded-lg border border-red-200">
          <h4 className="font-semibold text-red-800 mb-2 text-sm">🚨 Sobre a Taxa de {formatarPercentual(breakdown.taxaJurosMensal)}% ao Mês:</h4>
          <p className="text-xs text-red-700 leading-relaxed">
            Esta taxa representa <strong>{formatarMoedaPtBR((Math.pow(1 + breakdown.taxaJurosMensal, 12) - 1) * 100)}% ao ano</strong>. 
            É uma das maiores taxas do mercado financeiro brasileiro. <strong>Busque alternativas urgentemente!</strong>
          </p>
        </div>
      )}
    </MainCard>
  );
};